import React, { useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useNavigate, useParams } from 'react-router-dom';
import { auth, db, fileStorage, functions } from 'config/firebase';
import { doc, arrayUnion, query, updateDoc, collection, where, limit, FieldValue, arrayRemove } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { useHttpsCallable } from 'react-firebase-hooks/functions';
import {
  Card,
  Row,
  Col,
  Table,
  Tab,
  Tabs,
  Image,
  Alert,
  Form,
  InputGroup,
  Button,
  Accordion,
  Modal
} from 'react-bootstrap';
import Flex from 'components/common/Flex';
import ProductGenius from 'components/merlin/custom/ProductGenius';
import CompIntel from 'components/merlin/custom/CompIntel';
import ProductChannels from 'components/merlin/custom/productChannels';
import VariantList from 'components/merlin/custom/variantList';
import ProductDetails from 'components/merlin/custom/productDetails';
import MissingImage from '../../assets/merlin/missing_image.png';
import Tags from 'components/merlin/custom/tags';
import { useCollectionData, useDocumentData } from 'react-firebase-hooks/firestore';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagic, faCameraRetro } from '@fortawesome/free-solid-svg-icons'
import { v4 as uuidv4 } from 'uuid';
import ProductHistory from 'components/merlin/custom/ProductHistory';
import FavoriteHeart from 'components/merlin/custom/FavoriteHeart';

const Products = () => {
  let { sku } = useParams();
  const [user, loading] = useAuthState(auth);
  const navigate = useNavigate();
  const [bestImg, setBestImg] = useState(MissingImage);
  const [bestTitle, setBestTitle] = useState('Loading...');
  const [productStatus, setProductStatus] = useState(true);
  const [editActive, setEditActive] = useState(false);
  const [barcodes, setBarcodes] = useState([]);
  const [newImageFile, setNewImageFile] = useState("");
  const [pictureUploadProgress, setPercent] = useState(0)
  const [srp, setSRP] = useState();
  const [retailSRP, setRetailSRP] = useState();
  const [transferAmount, setTransferAmount]  = useState(0);
  const [pictureModalState, showPictureModal] = useState(false);
  const [enhanceModalState, showEnhanceModal] = useState(false);
  const [enhancedId, setEnhancedId] = useState();
  const [downloadURL, setDownloadURL] = useState();

  const [product, docLoading, docError, docSnapshot ] = useDocumentData(doc(db, 'products', sku), {
    snapshotListenOptions: { includeMetadataChanges: true }
  });

  const [enhancedProducts, enhancedLoading, enhancedError, enhancedSnapshot ] = useCollectionData(
    query(
      collection(db, "enhancedProductData"),
      where("parentSku", "==", sku),
      limit(1)
    ),
    { snapshotListenOptions: { includeMetadataChanges: true } }
  );

  const [enhanceProductData, enhanceExecuting, enhanceError] = useHttpsCallable(functions, 'enhanceProductData');
  const [getProductBarcodes, barcodesExecuting, barcodesError] = useHttpsCallable(functions, 'getProductBarcodes');
  const [checkDataExpiry, expiryExecuting, expiryError] = useHttpsCallable(functions, 'checkEnhancedDataExpiration');
  const [downloadRemoteImage, downloadExecuting, downloadError] = useHttpsCallable(functions, 'downloadRemoteImage');
  
  /**
   * Primary Picture Handlers
   */
  const handlePictureClose = () => {
    showPictureModal(false);
    setNewImageFile("");
    setPercent(0);
  };
  const handlePictureShow = () => showPictureModal(true);
  const handleFileSelect = (e) => setNewImageFile(e.target.files[0]) 
  const handleURLSelect = (e) => setDownloadURL(e.target.value);
  
  function handleFileUpload() {
    if (!newImageFile) alert('Choose a file first')

    const filename = newImageFile.name.split(".");
    // create a unique name and save with original file/filename suffix
    const storageRef = ref(fileStorage, `/images/products/${uuidv4()}.${filename[filename.length - 1]}`)
    const uploadTask = uploadBytesResumable(storageRef, newImageFile);

    uploadTask.on("state_changed", 
      (snapshot) => {
        const percent = Math.round( (snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        setPercent(`${Number(percent)}%`);
      }, 
      (err) => console.log(err), 
      (success) => {
        // download url, the success object isn't real
        getDownloadURL(uploadTask.snapshot.ref).then(async (url) => {
          setPercent("Success")
          //add url to images array in product collection
          const prodRef = doc(db, "products", sku);
          await updateDoc(prodRef, {
            images: arrayUnion(url)
          });
          // sku is set in component useState
          changePrimaryImg(sku, url);
          handlePictureClose();
         });
      }
    );

  }

  async function handleURLFileUpload(e) {
    e.preventDefault();
    console.log(downloadURL);
    await fetch(downloadURL)
    .then(res => res.blob()) // Gets the response and returns it as a blob
    .then(blob => {
      // Here's where you get access to the blob
      // And you can use it for whatever you want
      // Like calling ref().put(blob)
      console.log(blob);
  });

  }

  async function changePrimaryImg(sku, url) {
    try {
      console.log(url);
      if (!sku) return Promise.reject("No SKU")
      if (!url) return Promise.reject("Must include picture URL")
      const productRef = doc(db, "products", sku)
      return await updateDoc(productRef, {
        primaryImg: url
      });
    } catch (error) {
      console.log(error);
    }
  }

  async function removeImageFromGallery(sku, url) {
    try {
      const filename = url.split("?");
      console.log('remove', filename[0]);
      const docRef = doc(db, "products", sku);
      const myArrayRemove = arrayRemove(url);
      await updateDoc(docRef, {
        images: myArrayRemove
      })

    } catch (error) {
      console.log(error);
    }
  }

  /**
   * Enhance Product Data Handlers
   */
  const [enhanceLifecycleMessage, setEnhanceLifecycleMessage] = useState("");
  const [enhancementDisabledState, setEnhancementDisabledState] = useState(false);
  const handleEnhanceClose = () => showEnhanceModal(false);
  const handleEnhanceShow = () => showEnhanceModal(true);
  const EnhanceIcon = <FontAwesomeIcon icon={faMagic} />
  const CameraIcon = <FontAwesomeIcon icon = {faCameraRetro} />

  function ThumbNailImage(img) {
    const url = img.img;
    const sku = img.sku
    return (
      <Card className='mt-5 m-3' style={{width: '10rem'}}>
        <p></p>
        <Image src={url} fluid/> 
        <p></p>
        <Button
          size='sm' 
          className='mb-2'
          onClick={async () => {
            try {
              await changePrimaryImg(sku, url)
              handlePictureClose();
            } catch (error) {
              console.log(error)
            } 
          }}
          disabled={docLoading}
        >Make Primary</Button>
        
        <Button
          className='mb-3'
          size='sm'
          variant='secondary' 
          onClick={async () => {
            try {
              await removeImageFromGallery(sku, url)
            } catch (error) {
              console.log(error)
            }
          }}
          disabled={docLoading}
        >Remove </Button>

      </Card>
    )
  }

  function ImageGallery(images) {
    const allImages = images.images;
    const imgDisplay = [];
    if (typeof allImages !== 'undefined') {
      allImages.forEach((img) => {
        imgDisplay.push(<ThumbNailImage img={`${img}`} sku={sku} />)
      })  
    }

    return (
      <>
        <Row xs={1} md={2} className="g-4">
          {imgDisplay.length > 0 ? imgDisplay : <Col>No images available please upload or import from Product Genius</Col>}
        </Row>
      </>
    )
  }

  async function handleEnhancementClick(e, sku) {
    e.preventDefault();
    setEnhancementDisabledState(true);
    try {
      
      const expiryCheck = await checkDataExpiry({sku: sku});

      if (expiryCheck.data === 'REFRESH DATA') {
        let barcodes = await getProductBarcodes({sku: sku});
        if(!barcodes) {
          setEnhanceLifecycleMessage("No barcode available")
        } else {
          let result = await enhanceProductData({sku: sku, barcodes: barcodes.data});
          setEnhanceLifecycleMessage(result.data);
        }
        
      }

      if (expiryCheck.data === 'NO REFRESH') {
        setEnhanceLifecycleMessage("No refresh needed...");
      }
      
      setEnhancementDisabledState(false);
    } catch (error) {
      setEnhanceLifecycleMessage("There was an error")
      setEnhancementDisabledState(false);
      console.log(error, expiryError, enhanceError);
    }
  }

  useEffect(() => {
    if (loading) return;
    if (!user) return navigate('/');

    if(!docLoading) {
      setBestTitle(product?.sell_Title || product?.ibmi_name)
      setBestImg(product?.primaryImg || MissingImage)
      let size = product?.ibmi_size;
      let numInCaseRaw = size.split('/');
      let numInCase = Number(numInCaseRaw[0]);
      if (isNaN(numInCase)) numInCase = 1;
      if (product.ibmi_srp === 'None') {
        let newMarkup = Number(product.ibmi_cost) * Number(0.40);
        let srp = Math.round(((Number(product.ibmi_cost) + newMarkup) + Number.EPSILON) * 100) / 100;
        setSRP(srp);
        let eachPrice = srp / numInCase;
        eachPrice = Number(Math.ceil(eachPrice * 100) / 100);
        setRetailSRP(eachPrice);
      } else {
        setSRP( Number((product.ibmi_srp / 100) * numInCase))
        //console.log(srp, numInCase)
        let tempEachPrice = Number(Math.round( ((srp / numInCase) + Number.EPSILON)));
        setRetailSRP(tempEachPrice);
      }
      
    }
    
  }, [user, loading, product, navigate, sku, srp, retailSRP]);

  return product && !docLoading ? (
    <>
      <Card className="mb-3">
        <Card.Body>
          <Row>
              <Col
                as={'flex'}
                justifyContent="center"
                className="mb-4 mb-lg-0"
              >
              <Image className={""} src={bestImg} fluid/>
              
            </Col>
            <Col lg={6} as={Flex} justifyContent="start" direction="column">
              {productStatus ? (
                <h4>{bestTitle}</h4>
              ) : (
                <>
                  <Alert variant="danger">
                    Warning Product is Discontinued or Substituted
                    <h4>{product.ibmi_name || product.sell_Title}</h4>
                  </Alert>
                </>
              )}

              <Table bordered hover>
                <tbody>
                  <tr>
                    <td>SKU</td>
                    <td>{product.ibmi_sku}</td>
                  </tr>
                  <tr>
                    <td>E-Commerce On Hand</td>
                    <td>{product.ecomm_onhand}</td>
                  </tr>
                  <tr>
                    <td>Reserved Quantity</td>
                    <td>{product.ibmi_reserve_qty}</td>
                  </tr>
                  <tr>
                    <td>IBMi On Hand</td>
                    <td>{product.ibmi_onhand}</td>
                  </tr>
                  <tr>
                    <td>Warehouse Slot</td>
                    <td>{product.ibmi_slot}</td>
                  </tr>
                  <tr>
                    <td>Cost</td>
                    <td>${product.ibmi_cost.slice(0, -2)}</td>
                  </tr>
                  <tr>
                    <td>Suggested Full Case Price</td>
                    <td>${srp}</td>
                  </tr>
                  <tr>
                    <td>Suggested Each Price</td>
                    <td>${retailSRP}</td>
                  </tr>
                </tbody>
              </Table>
              <p>
                
              </p>
              
            </Col>
          </Row>
          <Row>
            <Col>
              <div className='mt-3'>
                <FavoriteHeart sku={sku} name={bestTitle} picture={bestImg}/>{" "}
                
                
                <Button variant="falcon-primary" onClick={handleEnhanceShow} size="lg">
                  {EnhanceIcon}
                </Button>
                <Modal show={enhanceModalState} onHide={handleEnhanceClose}>
                  <Modal.Header closeButton>
                    <Modal.Title>Enhance Product Data</Modal.Title>

                  </Modal.Header>
                  <Modal.Body>
                    <div className='mb-3'>
                      Product data is enhanced from third-party data sources. Select below source to begin enhancement. After enhancement new data will be available under the 'Product Genius' tab.
                    </div>
                    <div className='mb-3'>
                      <h5>Select Enhancement Source</h5>
                    </div>
                    <div className='mb-3'>
                      <Button 
                        onClick={ (e) => handleEnhancementClick(e, sku)}
                        disabled={enhancementDisabledState}
                      >{enhancementDisabledState ? 'Loading' : 'barcodelookup.com' }</Button>
                    </div>
                    {enhanceLifecycleMessage}
                  </Modal.Body>
                  <Modal.Footer>
                    <Button variant="secondary" onClick={handleEnhanceClose}>
                      Close
                    </Button>
                  </Modal.Footer>
                </Modal>
                {" "}

                <Button variant="falcon-primary" onClick={handlePictureShow} size="lg">
                  {CameraIcon}
                </Button>
                <Modal size="lg" show={pictureModalState} onHide={handlePictureClose}>
                  <Modal.Header closeButton>
                    <Modal.Title>Change Product Photo</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    <div className='mb-3'>
                      <h5>Upload New Photo from File</h5>
                      <input onChange={handleFileSelect} type={'file'} accept="image/*" />
                      
                      <button onClick={handleFileUpload}>
                        {pictureUploadProgress != 0 ? 
                        `${pictureUploadProgress}`  : "Upload"}
                      </button>
                    </div>
                    <div className='mb-3'>
                      <h5>Upload New Photo from URL</h5>
                      <input onChange={handleURLSelect} id="downloadByURLInput" type={'text'} />
                      
                      <Button 
                      onClick={async () => {
                        try {
                          let download = document.getElementById("downloadByURLInput").value;
                          //console.log(download);
                          let url = await downloadRemoteImage({imgSrc: download, sku: sku})
                          console.log(url);
                        } catch (error) {
                          console.log(error)
                        }
                      }}
                      disabled={downloadExecuting}
                    >Grab Image</Button>
                    </div>
                    <p></p>
                    <div className='mb-3'>
                      <h5>Or select below</h5>
                      <ImageGallery images={product.images} />
                    </div>
                  </Modal.Body>
                  <Modal.Footer>
                    <Button variant="secondary" onClick={handlePictureClose}>
                      Close
                    </Button>
                  </Modal.Footer>
                </Modal>
                {" "}

              </div>
              <div className='mt-3'>
                <Form>
                <Accordion>
                <Accordion.Item eventKey="0">
                  <Accordion.Header>Transfer Inventory</Accordion.Header>
                  <Accordion.Body>
                  <Form.Label>From:</Form.Label>
                <Form.Select aria-label="Select inventory location to transfer from">
                  
                  <option defaultValue="0">Topicz Main Warehouse</option>
                  <option defaultValue="1">Inventory Room</option>
                </Form.Select>
                <Form.Label>To:</Form.Label>
                <Form.Select aria-label="Select inventory location to transfer from">
                  <option defaultValue="1">Inventory Room</option>
                  <option defaultValue="0">Topicz Main Warehouse</option>
                </Form.Select>
                <p></p>
                <InputGroup className="mb-6">
                  <Form.Control aria-label="" value={transferAmount} onChange={() => {}}/>
                  <Button variant="outline-secondary" onClick={() => setTransferAmount(transferAmount+1)}>+</Button>
                  <Button variant="outline-secondary" onClick={() => setTransferAmount(transferAmount-1)}>-</Button>
                  
                </InputGroup>

                <div className="d-grid gap-2">
                    <Button variant="primary" size="sm" onClick={() => {
                      setTransferAmount(0);
                      toast.success(`Inventory Transfered`, {
                        theme: 'colored'
                      });
                    }}>
                      Transfer
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => {
                      setTransferAmount(0);
                      toast.warn('Transfer Cancelled')
                    }}>
                      Cancel
                    </Button>
                  </div>
                  </Accordion.Body>
                </Accordion.Item>
                </Accordion>
                

              </Form>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <Card className="mb-3">
        <Card.Body>
          <Tags key={`${product.ibmi_sku}_tags`} sku={product.ibmi_sku} tags={product.tags} />
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body>
          <Tabs
            defaultActiveKey="details"
            id="product_tabs"
            className="mb-3"
            fill
          >
            <Tab eventKey="details" title="Product Details">
              <ProductDetails product={product} wandEdit={editActive} />
            </Tab>
            <Tab eventKey="channels" title="Channels">
              <ProductChannels product={product} />
            </Tab>
            <Tab eventKey="variants" title="Variants">
              <VariantList product={product} />
            </Tab>
            <Tab eventKey="genius" title="Product Genius">
              {!enhancedLoading ? <ProductGenius products={enhancedProducts} /> : "Loading..."}
            </Tab>
            <Tab eventKey="Comp Intel" title="Comp Intel">
              {!enhancedLoading ? <CompIntel products={enhancedProducts} /> : "Loading..."}
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body>
          <ProductHistory sku={sku} />
        </Card.Body>
      </Card>
    </>

  ) : (
    <h3>loading...</h3>
  );
};
//<ProductGenius barcodes={barcodes} />
export default Products;
